/* eslint-disable max-len */
/**
 * @file 销售路由
 * @author gaoxunchen
 */

import _ from 'lodash';
import React from 'react';
import { Loading } from '@tencent/ptp-fe-common';
import loadable from '@loadable/component';

import perms from '@/common/permissions/sale/perms';
import permsConfig from '@/common/permissions/sale/perms-config';
import { isOa } from '@/utils';

import { oneOf } from './utils';

export default [
  {
    label: '销售',
    key: 'sale-manage',
    desc: '支持对线索、商机、订单、外勤、客户进行查询和编辑',
    perm: (loginStatus: Record<string, any>) => !loginStatus.onlyTechAgency && loginStatus.hasSaleRole && (loginStatus.oneOf([...perms.all_leads, ...perms.all_potentials, ...perms.all_orders, ...perms.all_materials, perms.CHANNEL_ORDER_DOWNLOAD, ...perms.all_sales_performance_target]) || permsConfig.outside || permsConfig.onlineSales),
    data: [
      {
        label: '销售管理',
        data: [
          {
            label: '线索管理',
            href: '/leads',
            key: 'leads',
            perm: oneOf(perms.all_leads),
            data: [
              {
                label: '线索管理',
                href: '/leads',
                perm: oneOf(perms.all_leads),
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/leads'), { fallback: <Loading /> }),
              },
              {
                hide: true,
                label: '线索详情',
                href: '/leads/:leadId',
              },
              {
                hide: true,
                label: '创建线索',
                href: '/leads/create',
              },
              {
                hide: true,
                label: '修改线索',
                href: '/leads/:leadId/edit',
              },
            ],
          },
          {
            label: '商机管理',
            href: '/potentials',
            key: 'potentials',
            perm: oneOf(perms.all_potentials),
            data: [
              {
                label: '商机管理',
                href: '/potentials',
                perm: oneOf(perms.all_potentials),
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/potentials'), { fallback: <Loading /> }),
              },
              {
                hide: true,
                label: '商机详情',
                href: '/potentials/:leadId',
              },
              {
                hide: true,
                label: '修改商机',
                href: '/potentials/:leadId/edit',
              },
            ],
          },
          {
            label: '订单管理',
            href: '/orders',
            key: 'orders',
            perm: oneOf(perms.all_orders),
            data: [
              {
                label: '订单管理',
                href: '/orders',
                perm: oneOf(perms.all_orders),
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/orders'), { fallback: <Loading /> }),
              },
              {
                hide: true,
                label: '订单详情',
                href: '/orders/:leadId',
              },
              {
                hide: true,
                label: '修改订单线索信息',
                href: '/orders/:leadId/update-lead',
              },
            ],
          },
          {
            label: '公海池',
            href: '/seas/leads',
            path: '/seas/:type',
            key: 'seas',
            perm: oneOf([...perms.all_leads, ...perms.all_potentials]),
            data: [
              {
                label: '公海池',
                href: '/seas/leads',
                perm: oneOf([...perms.all_leads, ...perms.all_potentials]),
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/seas'), { fallback: <Loading /> }),
              },
              {
                label: '线索公海池',
                href: '/seas/leads',
                hide: true,
                perm: oneOf(perms.all_leads),
                key: 'seas-leads',
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/seas'), { fallback: <Loading /> }),
              },
              {
                label: '商机公海池',
                href: '/seas/potentials',
                hide: true,
                perm: oneOf(perms.all_potentials),
                key: 'seas-potentials',
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/seas'), { fallback: <Loading /> }),
              },
              {
                label: '活跃商机公海池',
                href: '/seas/potentials-active',
                hide: true,
                perm: (loginStatus: Record<string, any>) => {
                  // 包含灰度权限码，展示活跃商机
                  return _.includes(loginStatus.saleStatus?.grayPermissions, perms.GRAY_POTENTIAL_HIGH_SEAS_SILENT) && loginStatus.oneOf(perms.all_potentials);
                },
                key: 'seas-potentials-active',
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/seas'), { fallback: <Loading /> }),
              },
              {
                label: '沉默商机公海池',
                href: '/seas/potentials-silence',
                hide: true,
                perm: (loginStatus: Record<string, any>) => {
                  // 包含灰度权限码，展示沉默商机
                  return _.includes(loginStatus.saleStatus?.grayPermissions, perms.GRAY_POTENTIAL_HIGH_SEAS_SILENT) && loginStatus.oneOf(perms.all_potentials);
                },
                key: 'seas-potentials-silence',
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/seas'), { fallback: <Loading /> }),
              },
            ],
          },
          {
            label: '分发池',
            href: '/distributePool',
            perm: (loginStatus: Record<string, any>) => {
              const { saleStatus = {} } = loginStatus || {};
              const { isInProtectionWhiteList } = saleStatus;
              return isInProtectionWhiteList  // 是否在直销白名单内
                && loginStatus.oneOf([
                  perms.DISTRIBUTE_VIEW_SELF_AND_GROUP,
                  perms.DISTRIBUTE_VIEW_ALL]);
            },
            key: 'distribution_pool',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/leads/distribution'), { fallback: <Loading /> }),
            basePage: true,
          },
          // {
          //   label: '附近推',
          //   href: '/nearbypush',
          //   key: 'nearbypush',
          //   component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/nearbypush'), { fallback: <Loading /> }),
          //   perm: (loginStatus: Record<string, any>) => {
          //     return _.includes(loginStatus.saleStatus?.grayPermissions, perms.GRAY_NEIGHBOURHOOD_WECHAT) && loginStatus.oneOf([perms.NEIGHBOURHOOD_BASE_VIEW_SELF, perms.NEIGHBOURHOOD_BASE_VIEW_GROUP, perms.NEIGHBOURHOOD_MANAGE]);
          //   },
          // },
        ],
      },
      {
        label: '工作管理',
        data: [
          {
            label: '基础查询',
            href: '/basic-search',
            key: 'sale-basic-search',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/leads/basicSearch'), { fallback: <Loading /> }),
            perm: oneOf([perms.LEAD_VIEW_BASIC_SEARCH]),
            data: [],
          },
          {
            label: '外勤管理',
            href: '/outside',
            perm: permsConfig.outside,
            data: [
              {
                label: '外勤管理',
                href: '/outside',
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/outside'), { fallback: <Loading /> }),
                key: 'outside',
                perm: permsConfig.outside,
              },
              {
                hide: true,
                label: '查看详情',
                href: '/outside/detail/:outsideTicketId',
              },
              {
                hide: true,
                label: '查看商机详情',
                href: '/outside/potentials/:leadId',
              },
              {
                hide: true,
                label: '修改商机',
                href: '/outside/potentials/:leadId/edit',
              },
              {
                hide: true,
                label: '修改外勤单',
                href: '/outside/edit/:outsideTicketId',
              },
              {
                hide: true,
                label: 'IS 审核',
                href: '/outside/inside-review/:outsideTicketId',
              },
              {
                hide: true,
                label: 'OS 审核',
                href: '/outside/outside-review/:outsideTicketId',
              },
            ],
          },
          {
            label: '群发管理',
            href: '/agency/material-list/group-message',
            path: '/agency/material-list/:type',
            data: [{
              label: '群发管理',
              key: 'group-message-list',
              href: '/agency/material-list/group-message',
              component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/material/group-message/list'), { fallback: <Loading /> }),
              basePage: true,
              perm: (loginStatus: Record<string, any>) => {
                return loginStatus.oneOf([perms.GROUP_MESSAGE_VIEW, perms.GROUP_MESSAGE_EDIT]);
              },
            }, {
              label: '群发详情',
              key: 'group-message-info',
              href: '/agency/group-message-info',
              component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/material/group-message/info'), { fallback: <Loading /> }),
              basePage: true,
              hide: true,
              perm: (loginStatus: Record<string, any>) => {
                return loginStatus.oneOf([perms.GROUP_MESSAGE_VIEW, perms.GROUP_MESSAGE_EDIT]);
              },
            }, {
              label: '新建群发',
              key: 'group-message-edit',
              href: '/agency/group-message-edit',
              component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/material/group-message/edit'), { fallback: <Loading /> }),
              basePage: true,
              hide: true,
              perm: (loginStatus: Record<string, any>) => {
                return loginStatus.oneOf([perms.GROUP_MESSAGE_EDIT]);
              },
            }, {
              label: '素材中心',
              key: 'material-list',
              href: '/agency/material-list/material',
              component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/material/list'), { fallback: <Loading /> }),
              basePage: true,
              perm: (loginStatus: Record<string, any>) => {
                return loginStatus.oneOf([perms.MATERIAL_VIEW, perms.MATERIAL_EDIT]);
              },
            }, {
              label: '素材详情',
              key: 'material-info',
              href: '/agency/material-info',
              component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/material/info'), { fallback: <Loading /> }),
              basePage: true,
              hide: true,
              perm: (loginStatus: Record<string, any>) => {
                return loginStatus.oneOf([perms.MATERIAL_VIEW, perms.MATERIAL_EDIT]);
              },
            }, {
              label: '编辑素材',
              key: 'material-edit',
              href: '/agency/material-edit',
              component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/material/edit'), { fallback: <Loading /> }),
              basePage: true,
              hide: true,
              perm: (loginStatus: Record<string, any>) => {
                return loginStatus.oneOf([perms.MATERIAL_EDIT]);
              },
            }],
          },
          {
            label: '渠道订单',
            href: '/channel',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/channel'), { fallback: <Loading /> }),
            perm: (loginStatus: Record<string, any>) => (loginStatus.oneOf([perms.CHANNEL_ORDER_DOWNLOAD]) || isOa()),
          },
          {
            label: '企微客户管理',
            key: 'work_wechat_customer',
            href: '/agency/work-wechat-customer',
            perm: (loginStatus: Record<string, any>) => {
              const work_wechat_customer_perm = _.includes(loginStatus.saleStatus?.grayPermissions, perms.GRAY_ONLINE_SALES_AGENCY_BIND);
              if (isOa()) {
                return work_wechat_customer_perm && loginStatus.oneOf([perms.ORGANIZATION_ONLINE_SALES_VIEW]);
              }
              return work_wechat_customer_perm;
            },
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/work-wechat-customer'), { fallback: <Loading /> }),
            basePage: true,
          },
        ],
      },
      {
        label: '业务管理',
        data: [
          {
            label: '销售组织管理',
            href: '/admin/architecture',
            key: 'admin-architecture',
            perm: (loginStatus: Record<string, any>) => {
              if (loginStatus.hasSaleRole && isOa() && loginStatus.allOf([perms.ORGANIZATION_VIEW])) {
                return true;
              }
              return false;
            },
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/admin'), { fallback: <Loading /> }),
          },
          {
            label: '客户管理',
            href: '/customer',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/customer'), { fallback: <Loading /> }),
            key: 'customer',
            perm: oneOf([perms.CUSTOMER_ACCOUNT_MANAGE]),
          },
          {
            label: '企微标签管理',
            href: '/agency/work-wechat-tags',
            key: 'work_wechat_tags',
            basePage: true,
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/work-wechat-tags'), { fallback: <Loading /> }),
            perm: (loginStatus: Record<string, any>) => _.includes(loginStatus.saleStatus?.grayPermissions, perms.GRAY_ONLINE_SALES_AGENCY_BIND) && loginStatus.oneOf([perms.ONLINE_SALES_TAG_MANAGER]),
          },
          {
            label: '销售规则管理',
            href: '/admin/rule-config/rules',
            path: '/admin/rule-config/:type',
            key: 'rule-config',
            perm: (loginStatus: Record<string, any>) => loginStatus.hasSaleRole && oneOf([perms.RULE_MANAGE, perms.RULE_VIEW]),
            data: [
              {
                label: '规则管理',
                href: '/admin/rule-config/rules',
                perm: oneOf([perms.RULE_MANAGE, perms.RULE_VIEW]),
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/admin'), { fallback: <Loading /> }),
              },
              {
                label: '外勤审核配置',
                href: '/admin/rule-config/outside',
                perm: (loginStatus: Record<string, any>) => loginStatus.oneOf([perms.RULE_MANAGE, perms.RULE_VIEW]) && _.includes(loginStatus.saleStatus?.grayPermissions, perms.GRAY_IO_MANAGEMENT),
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/admin'), { fallback: <Loading /> }),
              },
              // {
              //   label: '线索清洗状态配置',
              //   href: '/admin/rule-config/clean-state',
              //   perm: (loginStatus: Record<string, any>) => loginStatus.oneOf([perms.RULE_MANAGE, perms.RULE_VIEW]),
              //   component: Admin,
              // },
              {
                label: '线索接收和分配规则',
                href: '/admin/rule-config/leads',
                perm: oneOf([perms.RULE_MANAGE, perms.RULE_VIEW]),
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/admin'), { fallback: <Loading /> }),
              },
              {
                label: '公私海规则',
                href: '/admin/rule-config/sea',
                perm: oneOf([perms.RULE_MANAGE, perms.RULE_VIEW]),
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/admin'), { fallback: <Loading /> }),
              },
            ],
          },
          {
            label: '分机管理',
            href: '/ucenter/seat',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/usercenter'), { fallback: <Loading /> }),
            key: 'ucenter-seat',
            perm: (loginStatus: Record<string, any>) => loginStatus.hasSaleRole && /[^CHANNELSOFT|^QIADN]/.test(loginStatus.saleStatus?.callCenter),
          },
          {
            label: '电话黑名单',
            href: '/ucenter/blacklist',
            key: 'ucenter-blacklist',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/usercenter'), { fallback: <Loading /> }),
            perm: (loginStatus: Record<string, any>) => loginStatus.hasSaleRole && loginStatus.oneOf(perms.ACCESS_CONTACT_BLACKLIST),
          },
        ],
      },
      {
        label: '业绩管理',
        data: [
          {
            label: '我的目标',
            href: '/target/my',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/saleTarget/modules/employee'), { fallback: <Loading /> }),
            perm: oneOf([perms.PERM_SALES_TARGET_VIEW_SELF, perms.PERM_SALES_TARGET_VIEW_SELF_SUBORDINATES]),
            data: [
            ],
          },
          {
            label: '有效新客目标管理',
            href: '/target/customer',
            key: 'target-customer',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/saleTarget/modules/newCustomer'), { fallback: <Loading /> }),
            perm: (loginStatus: Record<string, any>) => {
              // 有 oa 域有效新客权限就能看服务商有效新客
              if (isOa() && loginStatus.oneOf([perms.PERM_VALID_NEW_CUSTOMER_TARGET_OA_VIEW_SELF, perms.PERM_VALID_NEW_CUSTOMER_TARGET_OA_VIEW_ALL])) {
                return true;
              }
              // 没有 8225 灰度权限不显示
              if (!_.includes(loginStatus.saleStatus?.grayPermissions, perms.GRAY_SALES_NEW_CUSTOM)) {
                return false;
              }
              return loginStatus.oneOf([
                perms.PERM_SALES_TARGET_NEW_CUSTOM,
              ]);
            },
          },
          {
            label: '业绩目标管理',
            href: '/target/perform',
            key: 'target-perform',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/saleTarget/modules/performTarget'), { fallback: <Loading /> }),
            perm: oneOf([perms.PERM_SALES_PERFORMANCE_TARGET_AGENCY_VIEW_SELF_SUBORDINATES, perms.PERM_SALES_PERFORMANCE_TARGET_AGENCY_VIEW_ALL]),
            data: [
              {
                label: '修改目标',
                href: '/target/perform/edit',
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/saleTarget/modules/performTargetEdit'), { fallback: <Loading /> }),
                data: [],
              },
            ],
          },
          {
            label: '过程目标管理',
            href: '/target/progress',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/saleTarget/modules/progressTarget'), { fallback: <Loading /> }),
            perm: oneOf([perms.PERM_SALES_PROCESS_TARGET_AGENCY_VIEW_SELF_SUBORDINATES, perms.PERM_SALES_PROCESS_TARGET_AGENCY_VIEW_ALL]),
            data: [
              {
                label: '修改目标',
                href: '/target/progress/edit',
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/saleTarget/modules/progressTargetEdit'), { fallback: <Loading /> }),
                data: [],
              },
            ],
          },
        ],
      },
      {
        label: '销售工具',
        data: [
          {
            label: '销售宝典',
            href: '/sales-handbook',
            key: 'sale-handbook',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/saleTools/modules/handbook'), { fallback: <Loading /> }),
            // perm: oneOf([perms.LEAD_VIEW_BASIC_SEARCH]),
            data: [],
            basePage: true,
            isNew: true,
            desc: '您可以在销售宝典里查询销售话术、查看优秀案例和营销方案以及销售过程中的常见问题FAQ等。',
            popoverInfo: '您可以在销售宝典里查询销售话术、查看优秀案例和营销方案以及销售过程中的常见问题FAQ等。',
            perm: (loginStatus: Record<string, any>) => {
              // 没有 8230 灰度权限不显示
              return _.includes(loginStatus.saleStatus?.grayPermissions, perms.PERM_SALES_TARGET_HANDBOOK);
            },
          },
          {
            label: '销售宝典详情分享',
            href: '/sales-handbook/share-detail',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/saleTools/modules/handbookShareDetail'), { fallback: <Loading /> }),
            hide: true,
            data: [],
          },
        ],
      },
      {
        label: '销售报表',
        perm: (loginStatus: Record<string, any>) => loginStatus.hasSaleRole,
        data: [
          {
            label: '销售数据监控',
            href: '/workbench/data',
            key: 'sales-report-workbench-data',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/workbench')),
            perm: oneOf([perms.REPORT_SALES_MONITOR_REPORT]),
            data: [],
          },
          {
            label: '销售话务分析',
            href: '/reports/traffic/data',
            path: '/reports/traffic/:type',
            key: 'sales-report-traffic',
            data: [
              {
                label: '话务数据监控',
                href: '/reports/traffic/data',
                perm: oneOf([perms.REPORT_SALES_MONITOR_REPORT]),
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/reports/TrafficData')),
              },
              {
                label: '话务数据统计',
                href: '/reports/traffic/stats',
                perm: oneOf([perms.REPORT_TRAFFIC_REPORT]),
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/reports')),
              },
              {
                label: '话务明细',
                href: '/reports/traffic/details',
                perm: oneOf([perms.REPORT_TRAFFIC_REPORT]),
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/reports')),
              },
              {
                label: '坐席记录',
                href: '/reports/traffic/agent-record',
                perm: oneOf([perms.REPORT_SEAT_ANALYSIS_REPORT_VIEW_ALL]),
                component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/reports/AgentRecord')),
              },
            ],
          },
          {
            label: '销售业绩分析',
            href: '/reports/performance',
            key: 'sales-report-performance',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/reports')),
            perm: oneOf([perms.REPORT_PERFORMANCE_ANALYSIS_REPORT_VIEW_COST_FIELD, perms.REPORT_PERFORMANCE_ANALYSIS_REPORT_VIEW_ALL]),
          },
          {
            label: '销售业务分析',
            href: '/reports/business-analysis/conversion',
            path: '/reports/business-analysis/:type',
            key: 'sales-report-business-analysis',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/reports')),
            perm: oneOf(perms.REPORT_BUSINESS_ANALYSIS_REPORT_VIEW_ALL),
            data: [
              {
                label: '转化量',
                hide: true,
                href: '/reports/business-analysis/conversion',
              },
              {
                label: '转化率',
                hide: true,
                href: '/reports/business-analysis/conversion-rate',
              },
              {
                label: '效能评估',
                hide: true,
                href: '/reports/business-analysis/performance-evaluation',
              },
            ],
          },
          {
            label: '销售消耗报表',
            href: '/reports/cost-data',
            key: 'sales-report-cost-data',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/reports')),
            perm: oneOf([perms.REPORT_COST_REPORT_VIEW_ALL, perms.REPORT_COST_REPORT_VIEW_SELF_OR_GROUP]),
          },
          {
            label: '销售充值报表',
            href: '/reports/charge-data',
            key: 'sales-report-charge-data',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/reports')),
            perm: oneOf([perms.REPORT_RECHARGE_REPORT_VIEW_ALL, perms.REPORT_RECHARGE_REPORT_VIEW_SELF_OR_GROUP]),
          },
        ],
      },
      {
        label: '销售链路报表',
        data: [
          {
            href: '/agency/sales-link-report',
            key: 'sales-link-report',
            label: '线索处理分析',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/reports/SalesLinkReports')),
            basePage: true,
            perm: oneOf([perms.REPORT_LEAD_ANALYSIS_REPORT_VIEW_ALL, perms.REPORT_LEAD_ANALYSIS_REPORT_VIEW_SELF_OR_GROUP]),
          },
          {
            href: '/agency/potentials-analysis',
            key: 'potentials-analysis',
            label: '商机转化分析',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/reports/PotentialAnalysis')),
            basePage: true,
            perm: oneOf([perms.REPORT_LEAD_ANALYSIS_REPORT_VIEW_ALL, perms.REPORT_LEAD_ANALYSIS_REPORT_VIEW_SELF_OR_GROUP]),
          },
          {
            href: '/agency/order-analysis',
            key: 'order-analysis',
            label: '订单上线分析',
            component: loadable(() => import(/* webpackChunkName: 'sale' */ /* webpackMode: 'lazy' */ '@/pages/reports/OrderOnlineAnalysis')),
            basePage: true,
            perm: oneOf([perms.REPORT_LEAD_ANALYSIS_REPORT_VIEW_ALL, perms.REPORT_LEAD_ANALYSIS_REPORT_VIEW_SELF_OR_GROUP]),
          },
        ],
      },
    ],
  },
];
